import { ColumnDef, Getter } from "@tanstack/react-table";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SortingRule } from "react-table";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import useSWR from "swr";
import { GhostButtonMediumV2 } from "../../../../../../components/Buttons/Buttons";
import { DropDown } from "../../../../../../components/DropDown/DropDown";
import { DownloadIcon } from "../../../../../../components/Icons/Icons";
import { IDMobile } from "../../../../../../components/IDs/IDs";
import { DropdownContainer } from "../../../../../../components/Layout/Layout";
import { Pagination } from "../../../../../../components/Pagination/Pagination";
import { createChangePerPageHandler } from "../../../../../../components/Pagination/util";
import { StatusLeft } from "../../../../../../components/Status/Status";
import { Table } from "../../../../../../components/Table/Table";
import { TooltipPortal } from "../../../../../../components/TooltipModal/TooltipPortal";
import { TextXlSemibold } from "../../../../../../components/Typography/TypographyV2";
import { endpoints } from "../../../../../../endpoints";
import { ContentWrapper } from "../../../../../../layout/portalPageLayout";
import {
  SupportedLanguage,
  WithPagination,
} from "../../../../../../types/types";
import { UploadedAssetSchema } from "../../../../../../types/types.PIM";
import {
  defaultHandleSort,
  DEFAULT_PER_PAGE,
  formatDateTime,
  toTitleCase,
  useStoreState,
} from "../../../../../../util/util";
import { ScrollTableWrapper } from "../../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";

const AssetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const OverflowCell = styled.span`
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;

export const PreviousExternalAssetsUploads = ({
  triggerMutate,
}: {
  triggerMutate?: string;
}) => {
  const { t } = useTranslation();
  const { storefront_id, tenant_id } = useStoreState();

  const [sortingRules, setSortingRules] = useState<{
    sortBy?: string;
    orderBy: "asc" | "desc";
  }>({
    orderBy: "desc",
  });
  const [tableData, setTableData] = useState<UploadedAssetSchema[]>([]);
  const [offset, setOffset] = useState(0);
  const perPageItems = [10, 20, 50];
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [mutate_trigger_value, setMutateTriggerValue] = useState<string>();

  const getStatusColor = (status: UploadedAssetSchema["upload_status"]) => {
    switch (status) {
      case "pending":
        return "orange";
      case "processing":
        return "blue";
      case "completed":
        return "green";
      case "failed":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusText = (
    status: UploadedAssetSchema["upload_status"],
    t: (key: string) => string
  ) => {
    switch (status) {
      case "pending":
        return t("Pending");
      case "processing":
        return t("Processing");
      case "completed":
        return t("Completed");
      case "failed":
        return t("Failed");
      default:
        return t("Unknown");
    }
  };

  const handleDownload = useCallback(
    (upload_id: string, result_csv_blob_name?: string) => {
      const handleDownloadCSV = (csvContent?: string, name?: string) => {
        if (csvContent) {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(
            new Blob([csvContent], { type: "text/csv" })
          );
          link.download = name ?? "pim_assets_upload_template.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
        }
      };
      const downloadCSV = async (blob_name?: string) => {
        if (blob_name) {
          try {
            const { data } = await axios.get(
              endpoints.v2_storefronts_storefront_id_pim_assets_external_upload_id_csv(
                storefront_id,
                upload_id
              ),
              { responseType: "blob" }
            );
            const reader = new FileReader();
            reader.onload = () => {
              const csvContent = reader.result as string;
              handleDownloadCSV(csvContent, blob_name);
            };
            reader.readAsText(data);
          } catch (error) {
            console.error("Error downloading CSV:", error);
          }
        }
      };
      downloadCSV(result_csv_blob_name);
    },
    [storefront_id]
  );

  const buildTooltipHtml = (values: string[]) =>
    `<ul style="margin:0;padding:0 0 0 16px">${values
      .map((v) => `<li>${v}</li>`)
      .join("")}</ul>`;

  const tableColumns = useMemo<ColumnDef<UploadedAssetSchema>[]>(
    () => [
      {
        header: t("Upload ID"),
        accessorKey: "number",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <IDMobile>{getValue()}</IDMobile>
        ),
      },
      {
        header: t("Asset Type"),
        accessorKey: "asset_type",
        enableSorting: false,
        cell: ({
          row,
          getValue,
        }: {
          row: { original: UploadedAssetSchema };
          getValue: Getter<UploadedAssetSchema["asset_type"]>;
        }) => {
          const uniqueTypes = [
            ...new Set(
              row.original.assets.map((a) => a.asset_type).filter(Boolean)
            ),
          ].map(toTitleCase);
          if (uniqueTypes.length === 0) {
            return <span>{getValue() ? toTitleCase(getValue()) : "--"}</span>;
          }
          const displayText = uniqueTypes.join(", ");
          const tooltipHtml =
            uniqueTypes.length > 1 ? buildTooltipHtml(uniqueTypes) : undefined;
          return (
            <>
              <OverflowCell
                data-tip={tooltipHtml}
                data-for="external-uploads-asset-type-tooltip"
                data-html={tooltipHtml ? true : undefined}
              >
                {displayText}
              </OverflowCell>
              <TooltipPortal>
                <ReactTooltip
                  id="external-uploads-asset-type-tooltip"
                  place="top"
                  effect="solid"
                  multiline={true}
                />
              </TooltipPortal>
            </>
          );
        },
      },
      {
        header: t("Category"),
        accessorKey: "category",
        enableSorting: false,
        cell: ({
          row,
          getValue,
        }: {
          row: { original: UploadedAssetSchema };
          getValue: Getter<UploadedAssetSchema["category"]>;
        }) => {
          const uniqueCategories = [
            ...new Set(
              row.original.assets.map((a) => a.category).filter(Boolean)
            ),
          ].map(toTitleCase);
          if (uniqueCategories.length === 0) {
            return (
              <span>
                {getValue()?.name ? toTitleCase(getValue()?.name) : "--"}
              </span>
            );
          }
          const displayText = uniqueCategories.join(", ");
          const tooltipHtml =
            uniqueCategories.length > 1
              ? buildTooltipHtml(uniqueCategories)
              : undefined;
          return (
            <>
              <OverflowCell
                data-tip={tooltipHtml}
                data-for="external-uploads-category-tooltip"
                data-html={tooltipHtml ? true : undefined}
              >
                {displayText}
              </OverflowCell>
              <TooltipPortal>
                <ReactTooltip
                  id="external-uploads-category-tooltip"
                  place="top"
                  effect="solid"
                  multiline={true}
                />
              </TooltipPortal>
            </>
          );
        },
      },
      {
        header: t("Language"),
        accessorKey: "language",
        enableSorting: false,
        cell: ({ getValue }: { getValue: Getter<SupportedLanguage> }) => (
          <span>{getValue()?.toLocaleUpperCase()}</span>
        ),
      },
      {
        header: t("# of assets"),
        enableSorting: false,
        accessorKey: "number_of_assets",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: "Uploaded By",
        enableSorting: false,
        accessorKey: "modified_by",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: "Uploaded On",
        accessorKey: "modified_at",
        cell: (cell) => cell.renderValue(),
      },
      {
        header: t("Status"),
        accessorKey: "upload_status",
        enableSorting: false,
        cell: ({
          getValue,
        }: {
          getValue: Getter<UploadedAssetSchema["upload_status"]>;
        }) => (
          <StatusLeft
            color={getStatusColor(getValue())}
            text={getStatusText(getValue(), t)}
            textStyle={{ fontSize: "13px" }}
          />
        ),
      },
      {
        header: "",
        accessorKey: "download_url",
        enableSorting: false,
        cell: ({
          row: { original },
        }: {
          row: { original: UploadedAssetSchema };
        }) =>
          original.result_csv_blob_name ? (
            <GhostButtonMediumV2
              onClick={() =>
                handleDownload(original.id, original.result_csv_blob_name)
              }
              title={t("Log report")}
            >
              <DownloadIcon width={16} height={16} />
            </GhostButtonMediumV2>
          ) : (
            <></>
          ),
      },
    ],
    [handleDownload, t]
  );

  const {
    data: previousUploadsResponse,
    mutate,
    error,
  } = useSWR<WithPagination<{ data: UploadedAssetSchema[] }>>(
    [
      endpoints.v2_tenants_tenant_id_pim_assets_uploads(tenant_id),
      useMemo(
        () => ({
          params: {
            offset,
            limit: perPage,
            ...(sortingRules.sortBy ? { sort_by: sortingRules.sortBy } : {}),
            order_by: sortingRules.orderBy ?? "desc",
            external_assets: true,
          },
        }),
        [offset, perPage, sortingRules.orderBy, sortingRules.sortBy]
      ),
    ],
    { revalidateOnMount: true }
  );

  const handleSort = async (rules: SortingRule<object>[]) =>
    defaultHandleSort(rules, sortingRules, setSortingRules, setTableData);

  const changePerPage = createChangePerPageHandler(
    offset,
    setPerPage,
    setOffset
  );

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  useEffect(() => {
    const handlePreviousUpdatesData = ({
      data,
      pagination,
    }: WithPagination<{ data: UploadedAssetSchema[] }>) => {
      setTableData(
        data.map((data) => ({
          ...data,
          modified_at: formatDateTime(data.modified_at),
        }))
      );
      setTablePagination({
        perPage: perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };
    if (previousUploadsResponse) {
      handlePreviousUpdatesData(previousUploadsResponse);
    }
  }, [perPage, previousUploadsResponse]);

  useEffect(() => {
    if (triggerMutate && triggerMutate !== mutate_trigger_value) {
      mutate();
      setMutateTriggerValue(triggerMutate);
    }
  }, [mutate, mutate_trigger_value, triggerMutate]);

  const isLoading = !previousUploadsResponse && !error;

  return (
    <AssetsContainer>
      <TextXlSemibold>{t("Previous Uploads")}</TextXlSemibold>
      <div>
        <DropdownContainer>
          <DropDown
            items={perPageItems}
            activeItem={perPage}
            textLeft={t("items") + ":"}
            textRight={t(" Per Page")}
            direction={"right"}
            className={"per_Page"}
            clickHandler={changePerPage}
          />
        </DropdownContainer>
        <ContentWrapper>
          <ScrollTableWrapper maxHeight="400px">
            <Table
              columns={tableColumns}
              data={tableData}
              isLoading={isLoading}
              error={error}
              handleSort={handleSort}
              lastChildleftAlign={true}
            />
          </ScrollTableWrapper>
          <Pagination
            pagination={tablePagination}
            offset={offset}
            handlePageClick={changePage}
          />
        </ContentWrapper>
      </div>
    </AssetsContainer>
  );
};
